import {AxiosResponse} from "axios";
import React from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {Api} from "../../../../helpers/api";
import {errorMessages} from "../../../../helpers/errorMessage";
import {routingAddresses} from "../../../../helpers/routing";
import {ValidatedValue} from "../../../../helpers/validatedValue";
import Validator from "../../../../helpers/validator";
import i18n from "../../../../i18n/i18n";
import {HttpErrorModel} from "../../../../common/models/httpError";
import DropDown from "../../../../common/components/shared/dropDown/dropDown";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import TextInput from "../../../../common/components/shared/textInput/textInput";
import {LocationEntryModel, LocationType} from "../../../models/location";
import {LocationService} from "../../../services/locationService";
import "./locationForm.scss";
import Form from "../../../../common/components/shared/form/form";
import Row from "../../../../common/components/shared/row/row";
import {Utils} from "../../../../helpers/utils";
import Checkbox from "../../../../common/components/shared/checkbox/checkbox";
import {DropDownModel} from "../../../../common/models/dropDown";

interface LocationFormState {
    isSubmitButtonBlocked: boolean;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    isVisibleCheckboxes: boolean;
}

interface LocationFormProps extends RouteComponentProps {
    locationData?: LocationEntryModel | null;
    isUpdate: boolean;
}

class LocationForm extends React.Component <LocationFormProps, LocationFormState> {
    private apiKey!: ValidatedValue<string>;
    private locationType!: ValidatedValue<LocationType | string>;
    private name!: ValidatedValue<string>;
    private url!: ValidatedValue<string>;
    private description!: ValidatedValue<string>;
    private handleParkingOpeningStart!: ValidatedValue<boolean>;
    private handleCargoLoadingStart!: ValidatedValue<boolean>;
    private isDevice!: boolean;

    constructor(props: any) {
        super(props);
        const apiKeyMinLength = 32;

        const locationValidators = {
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        };

        const apiKeyValidators = {
            name: "valueHasCorrectLength",
            fn: () => Validator.minLength(this.apiKey.value, apiKeyMinLength),
            errorMessage: () => i18n.t("topology.locations.errors.apiKeyCorrectLength")
        };

        const apiKeyValidatorsForUpdate = {
            name: "valueHasCorrectLength",
            fn: () => (this.apiKey.value.length > 1 && Validator.minLength(this.apiKey.value, apiKeyMinLength)) || this.apiKey.value === "",
            errorMessage: () => i18n.t("topology.locations.errors.apiKeyCorrectLength")
        };

        this.apiKey = this.props.isUpdate ? new ValidatedValue("", [apiKeyValidatorsForUpdate]) : new ValidatedValue("", [locationValidators, apiKeyValidators]);
        this.locationType = new ValidatedValue("", [locationValidators]);
        this.name = new ValidatedValue("", [locationValidators]);
        this.url = new ValidatedValue("", [locationValidators]);
        this.description = new ValidatedValue("");
        this.handleParkingOpeningStart = new ValidatedValue(false);
        this.handleCargoLoadingStart = new ValidatedValue(false);
        this.isDevice = false;

        this.state = {
            isSubmitButtonBlocked: false,
            response: undefined,
            isVisibleCheckboxes: false
        };
    }

    async componentDidMount() {
        this.setLocationData();
    }

    render(): JSX.Element {
        return (
            <Form
                class="location-form"
                renderContent={this.renderContent()}
                renderError={this.renderErrors()}
                submit={this.submit.bind(this)}
                backAddress={routingAddresses.topology.locations.list}
                isSubmitButtonBlocked={this.state.isSubmitButtonBlocked}
            />
        );
    }

    private renderErrors(): JSX.Element | undefined {
        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={this.getErrorMessage}
                    translatedFields={{
                        apiKey: i18n.t("topology.locations.httpErrors.apiKey"),
                        locationType: i18n.t("topology.locations.httpErrors.locationType"),
                        name: i18n.t("topology.locations.httpErrors.name"),
                        url: i18n.t("topology.locations.httpErrors.url"),
                    }}
                    translatedFieldsForEmpty={{
                        apiKey: i18n.t("topology.locations.httpErrors.apiKeyEmpty"),
                        locationType: i18n.t("topology.locations.httpErrors.locationTypeEmpty"),
                        name: i18n.t("topology.locations.httpErrors.nameEmpty"),
                        url: i18n.t("topology.locations.httpErrors.urlEmpty"),
                    }}
                    object={i18n.t("topology.locations.httpErrors.object")}
                />
            );
        }
    }

    private renderContent(): JSX.Element {
        this.isDevice =
            this.locationType.value === LocationType.PHOTOSHOOTER ||
            this.locationType.value === LocationType.ENTRY_TERMINAL ||
            this.locationType.value === LocationType.EXIT_TERMINAL ||
            this.locationType.value === LocationType.LIGHTBOARD;

        return (
            <>
                <Row class="flex-type gap-30">
                    <DropDown
                        className="element"
                        label={i18n.t("topology.locations.headers.locationType")}
                        validatedValue={this.locationType}
                        items={LocationForm.locationTypesToDropDownMap}
                        testId="location-type"
                        disabled={this.props.isUpdate}
                        onChange={(value) => {
                            this.addUrlValidators();
                            this.setVisibleCheckboxes(value);
                        }}
                    />
                    <TextInput
                        className="element" label={i18n.t("topology.locations.headers.name")}
                        validatedValue={this.name} testId="location-name"/>
                    {this.isDevice && (
                        <>
                            <TextInput
                                className="element" label={i18n.t("topology.locations.headers.url")}
                                validatedValue={this.url} testId="location-url"
                            />
                            <TextInput
                                className="element" label={i18n.t("topology.locations.headers.apiKey")}
                                validatedValue={this.apiKey} testId="location-api-key"
                            />
                        </>
                    )}
                </Row>
                <Row class="flex-type gap-30">
                    <TextInput
                        className="element" label={i18n.t("topology.locations.headers.description")}
                        validatedValue={this.description} testId="location-description"/>
                </Row>
                {this.state.isVisibleCheckboxes && this.renderCheckboxes()}
            </>
        );
    }

    private renderCheckboxes(): JSX.Element {
        return (
            <Row class="flex-type gap-30">
                <Checkbox
                    label={i18n.t("topology.locations.checkboxLabel.openParking")}
                    size="medium"
                    validatedValue={this.handleParkingOpeningStart}
                    testId="open-parking-checkbox"
                />
                <Checkbox
                    label={i18n.t("topology.locations.checkboxLabel.startLoading")}
                    size="medium"
                    validatedValue={this.handleCargoLoadingStart}
                    testId="start-loading-checkbox"
                />
            </Row>
        );
    }

    private addUrlValidators(): void {
        const urlWithTCPValidators = {
            name: "correctUrlWithTCP",
            fn: Validator.isCorrectAddressUrlWithTCP,
            errorMessage: () => i18n.t("topology.locations.errors.invalidUrl")
        };

        const urlWithValidators = {
            name: "correctUrl",
            fn: Validator.isCorrectAddressUrl,
            errorMessage: () => i18n.t("topology.locations.errors.invalidUrl")
        };

        const requiredValidators = {
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        };

        this.url.clearValidation();
        this.url.removeValidators();

        if (this.locationType.value === LocationType.LIGHTBOARD) {
            this.url.addValidators([urlWithTCPValidators]);
        } else {
            this.url.addValidators([urlWithValidators]);
        }
        this.url.addValidators([requiredValidators]);
    }

    private get getErrorMessage(): string {
        return this.props.isUpdate
            ? i18n.t("topology.locations.errors.modifyErrorSuffix")
            : i18n.t("topology.locations.errors.addErrorSuffix");
    }

    public get isValid(): boolean {
        if (this.isDevice) {
            this.apiKey.validate();
            this.url.validate();
        }
        this.locationType.validate();
        this.name.validate();
        this.description.validate();

        return this.apiKey.isValid && this.locationType.isValid && this.name.isValid &&
            this.url.isValid && this.description.isValid;
    }

    private setLocationData(): void {
        this.locationType.setValue(this.props.locationData?.locationType || "");
        this.name.setValue(this.props.locationData?.name || "");
        this.url.setValue(this.props.locationData?.url || "");
        this.description.setValue(this.props.locationData?.description || "");
        this.handleParkingOpeningStart.setValue(this.props.locationData?.handleParkingOpeningStart || false);
        this.handleCargoLoadingStart.setValue(this.props.locationData?.handleCargoLoadingStart || false);

        if (this.props.locationData && this.props.locationData?.locationType) {
            this.setVisibleCheckboxes(this.props.locationData?.locationType);
        }

        this.addUrlValidators();
    }

    private async submit(): Promise<void> {
        this.setState({response: undefined});

        if (this.isValid) {
            this.setState({isSubmitButtonBlocked: true});
            await this.addOrModifyLocation();
            this.setState({isSubmitButtonBlocked: false});

            if (Api.isSuccess(this.state.response)) {
                this.props.history.push(routingAddresses.topology.locations.list);
            }
        }
        Utils.scrollToFirstErrorMessage();
    }

    private async addOrModifyLocation(): Promise<void> {
        let handleParkingOpeningStart = false;
        let handleCargoLoadingStart = false;

        if (this.state.isVisibleCheckboxes) {
            handleParkingOpeningStart = this.handleParkingOpeningStart.value;
            handleCargoLoadingStart = this.handleCargoLoadingStart.value;
        }

        if (this.props.isUpdate && this.props.locationData?.id) {
            this.setState({
                response: await LocationService.modifyLocation({
                    apiKey: this.apiKey.value.trim(),
                    name: this.name.value.trim(),
                    url: this.url.value.trim(),
                    description: this.description.value.trim(),
                    locationType: this.locationType.value,
                    handleParkingOpeningStart,
                    handleCargoLoadingStart
                }, this.props.locationData?.id)
            });
        } else {
            this.setState({
                response: await LocationService.addLocation({
                    apiKey: this.apiKey.value.trim(),
                    name: this.name.value.trim(),
                    url: this.url.value.trim(),
                    description: this.description.value.trim(),
                    locationType: this.locationType.value,
                    handleParkingOpeningStart,
                    handleCargoLoadingStart
                })
            });
        }
    }

    private setVisibleCheckboxes(locationType: LocationType | string): void {
        switch (locationType) {
            case LocationType.LIGHTBOARD:
            case LocationType.ENTRY_TERMINAL:
            case LocationType.EXIT_TERMINAL:
                this.setState({isVisibleCheckboxes: true});
                break;
            default:
                this.setState({isVisibleCheckboxes: false});
        }
    }

    private static get locationTypesToDropDownMap(): DropDownModel[] {
        return [
            {
                id: LocationType.PHOTOSHOOTER,
                name: i18n.t("topology.locations.locationType.PHOTOSHOOTER")
            },
            {
                id: LocationType.ENTRY_TERMINAL,
                name: i18n.t("topology.locations.locationType.ENTRY_TERMINAL")
            },
            {
                id: LocationType.EXIT_TERMINAL,
                name: i18n.t("topology.locations.locationType.EXIT_TERMINAL")
            },
            {
                id: LocationType.LIGHTBOARD,
                name: i18n.t("topology.locations.locationType.LIGHTBOARD")
            },
            {
                id: LocationType.NO_AUTOMATION,
                name: i18n.t("topology.locations.locationType.NO_AUTOMATION")
            },
            {
                id: LocationType.PARKING,
                name: i18n.t("topology.locations.locationType.PARKING")
            },
            {
                id: LocationType.WHARF,
                name: i18n.t("topology.locations.locationType.WHARF")
            }
        ];
    }
}

export default withRouter(LocationForm);
